import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { AlertCircle, Activity, BarChart3, Bug, Link2, Play, ShieldAlert, TimerReset } from 'lucide-react';
import { toast } from 'sonner';
import { seoFetchService } from '@/services/seoFetchService';
import { seoDbService, SEOMetricInsert, SEOIssueInsert } from '@/services/seoDbService';

function analyzeHtml(html: string, pageUrl: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const title = doc.querySelector('title')?.textContent || '';
  const metaDescription = doc.querySelector('meta[name="description"]')?.getAttribute('content') || '';
  const h1Count = doc.querySelectorAll('h1').length;
  const images = Array.from(doc.querySelectorAll('img')) as HTMLImageElement[];
  const imagesWithoutAlt = images.filter(img => !img.getAttribute('alt') || img.getAttribute('alt')?.trim() === '').length;
  const links = Array.from(doc.querySelectorAll('a')) as HTMLAnchorElement[];
  const internalLinks = links.filter(a => (a.href || '').startsWith('https://seniorthrive.com') || (a.getAttribute('href') || '').startsWith('/')).length;
  const externalLinks = links.length - internalLinks;
  const text = doc.body?.textContent || '';
  const wordCount = text.split(/\s+/).filter(Boolean).length;

  // Simple readability proxy
  const sentenceCount = text.split(/[.!?]+/).filter(Boolean).length || 1;
  const readability = Math.max(0, Math.min(100, 100 - Math.round(wordCount / sentenceCount)));

  // Simple score calculation
  let score = 100;
  if (metaDescription.length < 50 || metaDescription.length > 160) score -= 10;
  if (h1Count !== 1) score -= 10;
  if (imagesWithoutAlt > 0) score -= Math.min(20, imagesWithoutAlt * 2);
  if (wordCount < 300) score -= 10;

  const issues: SEOIssueInsert[] = [];
  if (!title) {
    issues.push({ url: pageUrl, issue_type: 'meta', severity: 'critical', category: 'Meta', title: 'Missing <title>', description: 'Page is missing a title tag', fix_suggestion: 'Add a concise, keyword-rich title under 60 chars.' });
  }
  if (!metaDescription) {
    issues.push({ url: pageUrl, issue_type: 'meta', severity: 'warning', category: 'Meta', title: 'Missing meta description', description: 'No meta description found', fix_suggestion: 'Add a compelling description between 120-155 chars.' });
  }
  if (h1Count !== 1) {
    issues.push({ url: pageUrl, issue_type: 'content', severity: 'warning', category: 'Headings', title: 'H1 count should be exactly one', description: `Found ${h1Count} H1 tags`, fix_suggestion: 'Ensure a single, descriptive H1 per page.' });
  }
  if (imagesWithoutAlt > 0) {
    issues.push({ url: pageUrl, issue_type: 'images', severity: 'warning', category: 'Images', title: 'Images missing alt text', description: `${imagesWithoutAlt} images lack alt attributes`, fix_suggestion: 'Provide descriptive alt text with relevant keywords.' });
  }

  const metrics: SEOMetricInsert = {
    url: pageUrl,
    page_title: title,
    meta_description: metaDescription,
    h1_count: h1Count,
    images_without_alt: imagesWithoutAlt,
    total_images: images.length,
    internal_links: internalLinks,
    external_links: externalLinks,
    word_count: wordCount,
    readability_score: readability,
    seo_score: Math.max(0, score),
  };

  return { metrics, issues };
}

const SEOMonitoringDashboard: React.FC = () => {
  const [crawlPages, setCrawlPages] = useState(20);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [summary, setSummary] = useState<{openIssues: number; criticalIssues: number; avgScore: number; pagesAnalyzed: number}>({ openIssues: 0, criticalIssues: 0, avgScore: 0, pagesAnalyzed: 0 });

  const refreshSummary = useCallback(async () => {
    const s = await seoDbService.getDashboardSummary();
    setSummary(s);
  }, []);

  useEffect(() => {
    refreshSummary();
  }, [refreshSummary]);

  const runCrawlAndSave = useCallback(async () => {
    try {
      setIsRunning(true);
      setProgress(0);
      toast.info('Starting site crawl...', { description: `Analyzing up to ${crawlPages} pages` });

      const crawl = await seoFetchService.crawlFullSite(crawlPages) as any;
      
      // Prepare pages to analyze (from immediate result or client-side fallback)
      let toAnalyze: Array<{ url: string; html: string; status: number }> = [];

      if (crawl && crawl.results && Array.isArray(crawl.results)) {
        toAnalyze = crawl.results.filter((r: any) => r.status === 200 && r.html);
      } else {
        // Background crawl response detected — run a client-side crawl so we can analyze now
        toast.info('Large crawl running in background', { 
          description: `Starting immediate client-side crawl of up to ${crawlPages} pages.` 
        });
        const { urls } = await seoFetchService.fetchSitemap();
        const selected = (urls || []).slice(0, crawlPages);
        const batchSizeFetch = 5;
        for (let i = 0; i < selected.length; i += batchSizeFetch) {
          const batch = selected.slice(i, i + batchSizeFetch);
          const fetched = await Promise.all(
            batch.map(async (u) => {
              try {
                const { html, url: finalUrl } = await seoFetchService.fetchHtml(u);
                return { url: finalUrl, html, status: 200 };
              } catch (err) {
                console.warn('Failed to fetch page for analysis:', u, err);
                return null;
              }
            })
          );
          toAnalyze.push(...(fetched.filter(Boolean) as Array<{ url: string; html: string; status: number }>));
          setProgress(Math.round(((i + batch.length) / selected.length) * 50)); // fetching stage uses first 50%
          await new Promise(res => setTimeout(res, 60));
        }
      }

      const batchSize = 5;
      const allMetrics: SEOMetricInsert[] = [];
      const allIssues: SEOIssueInsert[] = [];

      for (let i = 0; i < toAnalyze.length; i += batchSize) {
        const batch = toAnalyze.slice(i, i + batchSize);
        batch.forEach(item => {
          try {
            const { metrics, issues } = analyzeHtml(item.html || '', item.url);
            allMetrics.push(metrics);
            allIssues.push(...issues);
          } catch (analysisError) {
            console.warn('Failed to analyze page:', item.url, analysisError);
          }
        });
        setProgress(50 + Math.round(((i + batch.length) / toAnalyze.length) * 50));
        await new Promise(res => setTimeout(res, 80));
      }

      // Save to database with error handling
      let savedMetrics = 0, savedIssues = 0;
      
      if (allMetrics.length > 0) {
        try {
          const { error: metricsError } = await seoDbService.saveMetrics(allMetrics);
          if (metricsError) {
            console.error('Failed to save metrics:', metricsError);
            toast.error('Failed to save SEO metrics', { description: metricsError.message });
          } else {
            savedMetrics = allMetrics.length;
          }
        } catch (error) {
          console.error('Metrics save error:', error);
          toast.error('Error saving metrics');
        }
      }

      if (allIssues.length > 0) {
        try {
          const { error: issuesError } = await seoDbService.saveIssues(allIssues);
          if (issuesError) {
            console.error('Failed to save issues:', issuesError);
            toast.error('Failed to save SEO issues', { description: issuesError.message });
          } else {
            savedIssues = allIssues.length;
          }
        } catch (error) {
          console.error('Issues save error:', error);
          toast.error('Error saving issues');
        }
      }

      if (savedMetrics > 0 || savedIssues > 0) {
        toast.success('Crawl complete', { 
          description: `Saved ${savedMetrics} pages and ${savedIssues} issues` 
        });
      } else {
        toast.warning('Crawl completed but no data was saved', {
          description: 'Check database connection and permissions'
        });
      }
      setProgress(100);
      refreshSummary();
    } catch (e: any) {
      console.error(e);
      toast.error('Crawl failed', { description: e?.message || 'Unexpected error' });
    } finally {
      setIsRunning(false);
      setTimeout(() => setProgress(0), 1500);
    }
  }, [crawlPages, refreshSummary]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Activity className="h-5 w-5" /> SEO Monitoring</CardTitle>
          <CardDescription>Proactive monitoring with crawl, scoring, and issue tracking</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="rounded-lg border p-4">
              <div className="text-sm text-muted-foreground">Open Issues</div>
              <div className="mt-1 text-2xl font-semibold flex items-center gap-2"><Bug className="h-5 w-5" /> {summary.openIssues}</div>
            </div>
            <div className="rounded-lg border p-4">
              <div className="text-sm text-muted-foreground">Critical Issues</div>
              <div className="mt-1 text-2xl font-semibold flex items-center gap-2 text-destructive"><ShieldAlert className="h-5 w-5" /> {summary.criticalIssues}</div>
            </div>
            <div className="rounded-lg border p-4">
              <div className="text-sm text-muted-foreground">Avg SEO Score</div>
              <div className="mt-1 text-2xl font-semibold flex items-center gap-2"><BarChart3 className="h-5 w-5" /> {summary.avgScore}</div>
            </div>
            <div className="rounded-lg border p-4">
              <div className="text-sm text-muted-foreground">Pages Analyzed</div>
              <div className="mt-1 text-2xl font-semibold flex items-center gap-2"><Link2 className="h-5 w-5" /> {summary.pagesAnalyzed}</div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center gap-3">
            <div className="grid gap-1 w-full md:w-auto">
              <Label htmlFor="crawlPages">Pages to crawl</Label>
              <Input id="crawlPages" type="number" min={5} max={200} value={crawlPages} onChange={e => setCrawlPages(Number(e.target.value))} className="w-40" />
            </div>
            <Button onClick={runCrawlAndSave} disabled={isRunning} className="gap-2">
              <Play className="h-4 w-4" /> {isRunning ? 'Running…' : 'Crawl & Save'}
            </Button>
            {isRunning && (
              <div className="flex items-center gap-3 w-full md:w-1/2">
                <Progress value={progress} className="w-full" />
                <span className="text-sm text-muted-foreground min-w-10 text-right">{progress}%</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SEOMonitoringDashboard;
